// @ts-strict-ignore
import React, { useState, useMemo } from 'react';
import type {
  Option,
} from '@usealloy/component-library';
import {
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  HelpText,
  MultiSelect,
  TextField,
} from '@usealloy/component-library';

import { generatePublicId } from '~/dashboard/views/workflows/store/engine/utils';
import BaseAddActionOption from '~/dashboard/views/workflows/editor/components/MenuBar/MenuBarModals/AddActionModals/BaseAddActionModal/BaseAddActionOption';
import { DISPLAY_NAME_OPTION_KEY } from 'flow/service/actionOptions';
import type {
  FormattedServiceConfigOption,
} from './types';
import { ServiceConfigModalMode, DefaultLevel } from './types';
import {
  StyledTextField,
} from './styles';
import { formatValue } from './helpers/formatData';

interface ServiceConfigOptionCreationProps {
  mode;
  serviceConfigOptions;
  formattedServiceConfigOptions: Array<FormattedServiceConfigOption>;
  cancelAdding: () => void;
  appendServiceConfigOption;
  selectedAlloyOptions;
  selectedCustomerOptions;
  submitAutoSave?: () => {};
}

export const ServiceConfigOptionCreation = ({
  mode,
  serviceConfigOptions,
  formattedServiceConfigOptions,
  cancelAdding,
  appendServiceConfigOption,
  selectedAlloyOptions,
  selectedCustomerOptions,
  submitAutoSave,
}: ServiceConfigOptionCreationProps) => {
  const [selectedOption, setSelectedOption] = useState<Option>();
  const [value, setValue] = useState<any>(undefined);

  const addServiceConfig = () => {
    const selectedOptionMeta = serviceConfigOptions.find(
      option => option.serviceConfigOptionId === selectedOption?.value,
    );
    const alloyOption = selectedAlloyOptions?.serviceConfigOptions?.find(
      option => option.serviceConfigOptionId === selectedOption?.value,
    );
    const customerOption = selectedCustomerOptions?.serviceConfigOptions?.find(
      option => option.serviceConfigOptionId === selectedOption?.value,
    );
    switch (mode) {
      case ServiceConfigModalMode.ALLOY: {
        appendServiceConfigOption({
          serviceConfigOptionId: selectedOptionMeta.serviceConfigOptionId,
          value,
        });
        break;
      }

      case ServiceConfigModalMode.CUSTOMER: {
        appendServiceConfigOption({
          serviceConfigOptionId: selectedOptionMeta.serviceConfigOptionId,
          value,
          defaultValue: alloyOption ? formatValue(alloyOption, selectedOptionMeta) : null,
          isUsingDefault: false,
          defaultLevel: alloyOption ? DefaultLevel.ALLOY : DefaultLevel.NULL,
        });
        break;
      }

      case ServiceConfigModalMode.NODE: {
        let defaultValue = null;
        let defaultLevel = DefaultLevel.NULL;
        if (customerOption) {
          defaultValue = formatValue(customerOption, selectedOptionMeta);
          defaultLevel = DefaultLevel.CUSTOMER;
        } else if (alloyOption) {
          defaultValue = formatValue(alloyOption, selectedOptionMeta);
          defaultLevel = DefaultLevel.ALLOY;
        }

        appendServiceConfigOption({
          serviceConfigOptionId: selectedOptionMeta.serviceConfigOptionId,
          value,
          defaultValue,
          isUsingDefault: false,
          defaultLevel,
          key: selectedOptionMeta.slug,
          publicId: generatePublicId('actionoption'),
        });
        if (submitAutoSave) {
          submitAutoSave();
        }
        break;
      }
    }
  };

  const allOptions = useMemo(() => {
    const existingConfigOptions = formattedServiceConfigOptions.map(
      option => option.serviceConfigOptionId);
    return serviceConfigOptions.reduce((acc, option) => {
      if (!existingConfigOptions.includes(option.serviceConfigOptionId)) {
        if (mode === ServiceConfigModalMode.NODE && option.slug !== DISPLAY_NAME_OPTION_KEY) {
          acc.push({
            label: option.displayName,
            value: option.serviceConfigOptionId,
            meta: {
              name: option.displayName,
              description: option.description,
              toolTipProps: {
                content: option.description,
              },
            },
            disabled: false,
            shouldShowCheckBox: false,
            icon: null,
          });
        } else if (mode === ServiceConfigModalMode.CUSTOMER && option.category !== 'global') {
          acc.push({
            label: option.displayName,
            value: option.serviceConfigOptionId,
            meta: {
              name: option.displayName,
              description: option.description,
              toolTipProps: {
                content: option.description,
              },
            },
            disabled: false,
            shouldShowCheckBox: false,
            icon: null,
          });
        }
      }
      return acc;
    }, []);
  }, [serviceConfigOptions, formattedServiceConfigOptions]);

  const valueField = useMemo(() => {
    const selectOptionMeta = serviceConfigOptions.find(
      option => option.serviceConfigOptionId === selectedOption?.value);
    if (!selectOptionMeta) return '';
    switch (selectOptionMeta.datatype) {
      case 'number':
      case 'decimal':
        return (
          <StyledTextField
            type="number"
            step={selectOptionMeta.datatype === 'number' ? 1 : 0.1}
            label="Value"
            required
            onChange={({ target: { value: newValue } }) => {
              setValue(newValue);
            }}
            value={value}
            placeholder="Enter a number"
          />
        );
      case 'boolean':
        return (
          <MultiSelect
            label="Value"
            required
            onChange={(newOption: Option) => {
              setValue(newOption);
            }}
            options={[
              { label: 'True', value: 'true' },
              { label: 'False', value: 'false' },
            ]}
            selectedOptions={value}
            isMulti={false}
            placeholder="Select one"
          />
        );
      case 'singleselect':
      case 'multiselect': {
        const allPossibleValue = selectOptionMeta.possibleValues.map(pv => ({
          label: pv.displayName,
          value: pv.serviceConfigPossibleValueId.toString(),
        }));
        return (
          <MultiSelect
            label="Value"
            required
            placeholder={
              selectOptionMeta.datatype === 'multiselect' ? 'Select one or more' : 'Select one'
            }
            onChange={newOptions => {
              if (selectOptionMeta.datatype === 'multiselect') {
                setValue(newOptions);
              } else {
                setValue([newOptions]);
              }
            }}
            options={allPossibleValue}
            isSearchable
            selectedOptions={value}
            visibleSelectedOptions={selectOptionMeta.datatype === 'multiselect'}
            isMulti={selectOptionMeta.datatype === 'multiselect'}
          />
        );
      }
      default:
        return (
          <TextField
            type="text"
            label="Value"
            required
            onChange={({ target: { value: newValue } }) => {
              setValue(newValue);
            }}
            value={value}
            placeholder="Enter a value"
          />
        );
    }
  }, [selectedOption, serviceConfigOptions, value]);

  return (
    <Box
      gap={4}
      direction="column"
      justifyContent="flex-end"
      alignItems="flex-start"
      style={{
        margin: 'var(--cl-space-4)',
        padding: 'var(--cl-space-4)',
        backgroundColor: 'var(--cl-gray-100)',
        borderRadius: 'var(--cl-radius-3)',
      }}
    >
      <MultiSelect
        components={{
          Option: BaseAddActionOption,
        }}
        label="Configuration option"
        required
        onChange={(newOptions: Option) => {
          setValue('');
          setSelectedOption(newOptions);
        }}
        options={allOptions}
        isSearchable
        selectedOptions={selectedOption}
        visibleSelectedOptions={false}
        isMulti={false}
        placeholder="Select one"
      />
      {
        selectedOption && (
          // @ts-expect-error - the Option type is mis-mapped in the underlying library
          <HelpText>{selectedOption.meta?.description}</HelpText>
        )
      }
      {valueField}
      <Box
        direction="row"
        justifyContent="flex-end"
        gap={4}
      >
        <Button
          size={ButtonSize.small}
          onClick={cancelAdding}
        >Cancel
        </Button>
        <Button
          variant={ButtonVariant.primary}
          size={ButtonSize.small}
          onClick={() => {
            addServiceConfig();
            cancelAdding();
          }}
          disabled={!value}
        >Set
        </Button>
      </Box>
    </Box>
  );
};
